import { ComponentProps, ReactNode, Ref, useRef } from 'react';
import { AriaButtonProps, mergeProps, useButton, useFocusRing } from 'react-aria';

import composeRefs from '@seznam/compose-react-refs';

import Badge from '~/components/scales/Badge/Badge';
import { TooltipTriggerProps, pickTooltipTriggerProps } from '~/components/scales/Tooltip';
import { validateDataCy } from '~/components/scales/utils/data-cy';

type Props = TooltipTriggerProps & {
  autoFocus?: boolean;
  'aria-label': string;
  badge?: Omit<ComponentProps<typeof Badge>, 'className'>;
  /**
   * @deprecated Avoid relying on data-cy for tests. Prefer accessible queries:
   * `screen.getByRole('button', { name: 'aria-label' })`
   */
  'data-cy'?: string;
  ref?: Ref<HTMLButtonElement>;
  icon: ReactNode;
  isDisabled?: boolean;
  isPressed?: boolean;
  onClick?: AriaButtonProps['onPress'];
  type: 'primary' | 'secondary' | 'destructive';
};

export default function Button(props: Props) {
  const ref = useRef(null);

  const { isFocusVisible, focusProps } = useFocusRing();

  const { buttonProps } = useButton(
    {
      'aria-label': props['aria-label'],
      'aria-pressed': props.isPressed,
      autoFocus: props.autoFocus,
      isDisabled: Boolean(props.isDisabled),
      onPress: props.onClick,
    },
    ref
  );

  const classes = [
    'flex relative grow-0 shrink-0 h-10 w-10 icon-md items-center justify-center gap-1 rounded-full transition type-button',
  ];

  if (props.type === 'primary') {
    classes.push(
      props.isPressed ? 'bg-button-primary-pressed' : 'bg-button-primary',
      'text-type-secondary border-2 border-button-primary',
      'hover:enabled:bg-button-primary-hover hover:enabled:border-button-primary-hover',
      isFocusVisible ? 'outline' : 'outline-none',
      'disabled:text-type-inactive disabled:bg-button-inactive disabled:border-button-inactive'
    );
  } else if (props.type === 'secondary') {
    classes.push(
      props.isPressed ? 'bg-button-icon-pressed' : 'bg-button-icon',
      'text-type-primary',
      'hover:enabled:bg-button-icon-hover',
      isFocusVisible ? 'outline' : 'outline-none',
      'disabled:text-type-inactive disabled:border-button-inactive'
    );
  } else if (props.type === 'destructive') {
    classes.push(
      props.isPressed ? 'bg-button-destructive-pressed' : 'bg-button-icon',
      'text-button-destructive',
      'hover:enabled:bg-button-destructive-hover',
      isFocusVisible ? 'outline outline-button-destructive' : 'outline-none',
      'disabled:text-type-inactive disabled:border-button-inactive'
    );
  }

  validateDataCy(props['data-cy'], 'button');

  return (
    <button
      {...mergeProps(pickTooltipTriggerProps(props), buttonProps, focusProps)}
      ref={composeRefs(ref, props.ref)}
      className={[...classes].join(' ')}
      data-cy={props['data-cy']}
    >
      {props.icon}
      {props.badge && <Badge {...props.badge} className="absolute -right-1 -top-2" />}
    </button>
  );
}
